import { Box, chakra, IconButton, Text, VStack } from '@chakra-ui/react';
import dayjs from 'dayjs';

import { CalendarMonth, Close } from '@m/icons';

export function formatDateTimeField(date: Date | undefined, compact = false): string {
  if (!date) return 'Select date & time';
  // Compact fits side-by-side Starts/Ends fields (e.g. home hero) without clipping.
  return dayjs(date).format(compact ? 'MMM D, h:mm A' : 'MMM D, YYYY [at] h:mm A');
}

interface DateTimeFieldRowProps {
  /**
   * Shorter value format (`MMM D, h:mm A`) for narrow side-by-side fields.
   * Full format keeps year + "at" for single-column forms.
   */
  compact?: boolean;
  /** When true, the field is non-interactive (e.g. no search location yet). */
  disabled?: boolean;
  /** Shows an error border (e.g. past start / incomplete window). */
  invalid?: boolean;
  isActive?: boolean;
  label: string;
  /** When provided and a value is set, a clear (X) button replaces the calendar icon. */
  onClear?: () => void;
  onClick: () => void;
  placeholder?: string;
  /**
   * Value is fixed (event ticket window). Uses the read-only field tokens
   * (`fill.input.read-only` / `text.read-only`) and hides the clear control.
   * Prefer this over `disabled`, which is for "nothing picked yet".
   */
  readOnly?: boolean;
  value?: Date;
}

function fieldBorderColor({
  disabled,
  invalid,
  isActive,
}: {
  disabled: boolean;
  invalid: boolean;
  isActive: boolean;
}): 'stroke.border.default' | 'stroke.border.focused' | 'stroke.border.error' {
  if (invalid) return 'stroke.border.error';
  if (!disabled && isActive) return 'stroke.border.focused';
  return 'stroke.border.default';
}

export function DateTimeFieldRow({
  compact = false,
  disabled = false,
  invalid = false,
  label,
  value,
  isActive = false,
  onClick,
  onClear,
  placeholder = 'Select date & time',
  readOnly = false,
}: DateTimeFieldRowProps) {
  const hasValue = !!value;
  const interactive = !disabled && !readOnly;
  const borderColor = fieldBorderColor({ disabled, invalid, isActive: isActive && interactive });
  const showClear = hasValue && !!onClear && interactive;
  let valueColor: 'text.primary' | 'text.read-only' | 'text.secondary' = 'text.secondary';
  if (readOnly) valueColor = 'text.read-only';
  else if (hasValue && !disabled) valueColor = 'text.primary';
  let cursor: 'default' | 'not-allowed' | 'pointer' = 'pointer';
  if (disabled) cursor = 'not-allowed';
  else if (readOnly) cursor = 'default';

  return (
    <VStack align="stretch" gap={1} minW={0} w="full">
      <Text variant="SmallBodyMedium" color="text.secondary">
        {label}
      </Text>
      {/* Chrome + trigger/clear as siblings — never nest IconButton inside the field button. */}
      <Box
        display="flex"
        alignItems="center"
        h="48px"
        minW={0}
        w="full"
        borderRadius="6px"
        // Keep 2px always — swapping 1px↔2px shifts the field and can dismiss
        // an anchored popover (mouseup lands outside after the grow).
        borderWidth="2px"
        borderStyle="solid"
        borderColor={borderColor}
        bg={readOnly ? 'fill.input.read-only' : 'fill.input.filled'}
        opacity={disabled ? 0.5 : 1}
        gap={1}
        pl={3}
        pr={showClear ? 1 : 3}
      >
        <chakra.button
          type="button"
          display="flex"
          flex="1"
          minW={0}
          h="full"
          alignItems="center"
          gap={1}
          bg="transparent"
          cursor={cursor}
          disabled={disabled}
          // Not `disabled`: a locked event window must stay legible, it just
          // cannot be opened.
          aria-disabled={readOnly || undefined}
          aria-invalid={invalid || undefined}
          onClick={() => {
            if (interactive) onClick();
          }}
          _focusVisible={
            !interactive
              ? { outline: 'none', boxShadow: 'none' }
              : {
                  outline: '2px solid',
                  outlineColor: invalid ? 'stroke.border.error' : 'stroke.border.focused',
                  outlineOffset: '2px',
                }
          }
        >
          <Text
            flex={1}
            minW={0}
            variant="MainBodyRegular"
            color={valueColor}
            truncate
            textAlign="left"
          >
            {hasValue ? formatDateTimeField(value, compact) : placeholder}
          </Text>
          {!showClear && (
            <Text
              as="span"
              flexShrink={0}
              color={readOnly ? 'text.read-only' : 'text.secondary'}
              lineHeight={0}
            >
              <CalendarMonth />
            </Text>
          )}
        </chakra.button>
        {showClear && (
          <IconButton
            aria-label={`Clear ${label}`}
            variant="ghost"
            size="xs"
            flexShrink={0}
            onClick={onClear}
          >
            <Close />
          </IconButton>
        )}
      </Box>
    </VStack>
  );
}
